/**
 * SuggestionsQueue — data-steward review queue (Block 6.C.2 / ADR-021;
 * Phase 1.7 extension: UNIONs suggested_edits + suggested_resources).
 *
 * Steward-gated. List filtered by status (default Pending). Pending tab
 * surfaces BOTH proposed edits AND proposed new resources in one
 * chronological feed; status filters apply to both kinds in parallel.
 * Per Phase 1.7 contractor opener decision #2: clean data-model
 * separation (two tables) + unified queue UX via consume-side UNION.
 *
 * Per ADR-017 F1 (no modal); F2 (single page); F4 (always-visible filters).
 * Server-side the list endpoints scope on `read_all_suggestions` (steward
 * sees all; everyone else is forced to own) — the page gate mirrors THAT
 * capability (canReadAllSuggestions), not manage_lookups (fixed 2026-07-14;
 * the queue READ was mis-keyed on canAddLookup, which kept the read-only
 * board_observer out of a page its capability admits). The approve/reject
 * affordances inside SuggestionReviewPane / ResourceProposalReviewPane gate
 * separately on `approve` (their endpoints' gate), so read_all_suggestions
 * holders without approve get a read-only queue.
 */

import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canReadAllSuggestions } from '@/lib/capabilities';
import { suggestedEditsApi } from '@/lib/suggestedEdits';
import { suggestedResourcesApi } from '@/lib/suggestedResources';
import type {
  SuggestedEditListResponse,
  SuggestedEditStatus,
  SuggestedEditWithJoinDisplay,
  SuggestedResource,
  SuggestedResourcesListResponse,
  SuggestedResourceStatus,
} from '@/lib/types';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { SuggestionRow } from '@/components/SuggestionRow';
import { SuggestionReviewPane } from '@/components/SuggestionReviewPane';
import { ResourceProposalRow } from '@/components/ResourceProposalRow';
import { ResourceProposalReviewPane } from '@/components/ResourceProposalReviewPane';
import { TAB_RAIL_CLASSES } from '@/components/primitives/tabRail';
import { suggestionsQueueKey } from '@/lib/refreshFamily';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

const VALID_STATUSES: ReadonlyArray<SuggestedEditStatus> = [
  'pending',
  'approved',
  'rejected',
  'withdrawn',
];

const STATUS_LABELS: Record<SuggestedEditStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

/**
 * UNION row shape — discriminated on `kind` so the renderer picks the
 * right component (SuggestionRow vs ResourceProposalRow). Sort key
 * `created_at` is uniform across both source types.
 */
type UnionRow =
  | { kind: 'edit'; row: SuggestedEditWithJoinDisplay; created_at: string }
  | { kind: 'resource'; row: SuggestedResource; created_at: string };

export function SuggestionsQueue(): ReactNode {
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>();
  const { whoami } = useTenant();
  const contact = useSupportContact();
  const [searchParams, setSearchParams] = useSearchParams();

  const tenantSlug = routeTenantSlug ?? '';
  // The queue READ mirrors the list endpoints' scope capability
  // (read_all_suggestions) — owner + data_steward + the read-only
  // board_observer.
  const isSteward = canReadAllSuggestions(whoami);

  const statusParam = searchParams.get('status');
  const activeStatus: SuggestedEditStatus = VALID_STATUSES.includes(
    statusParam as SuggestedEditStatus,
  )
    ? (statusParam as SuggestedEditStatus)
    : 'pending';

  const editsInvalidationKey = useMemo(
    () => suggestionsQueueKey('edits', tenantSlug, activeStatus),
    [tenantSlug, activeStatus],
  );
  const resourcesInvalidationKey = useMemo(
    () => suggestionsQueueKey('resources', tenantSlug, activeStatus),
    [tenantSlug, activeStatus],
  );

  const editsQuery = useQuery<SuggestedEditListResponse>({
    queryKey: editsInvalidationKey,
    queryFn: ({ signal }) =>
      suggestedEditsApi.list(
        tenantSlug,
        { status: activeStatus, limit: 50 },
        signal,
      ),
    enabled: isSteward,
  });

  const resourcesQuery = useQuery<SuggestedResourcesListResponse>({
    queryKey: resourcesInvalidationKey,
    queryFn: ({ signal }) =>
      suggestedResourcesApi.list(
        tenantSlug,
        { status: activeStatus as SuggestedResourceStatus, limit: 50 },
        signal,
      ),
    enabled: isSteward,
  });

  if (!isSteward) {
    return (
      <div className="px-3 py-6">
        <h2 className="font-display text-lg text-sage-800 mb-2">Access denied</h2>
        <div
          role="alert"
          className="my-3 p-3 border border-pepper/30 bg-pepper-soft rounded-base text-sm text-ink"
        >
          {contactSentence(
            "This page is for the people who look after the Directory. Ask {contact_admin} if your work needs it.",
            contact,
          )}
        </div>
      </div>
    );
  }

  function handleStatusChange(next: SuggestedEditStatus): void {
    const sp = new URLSearchParams(searchParams);
    sp.set('status', next);
    setSearchParams(sp, { replace: true });
  }

  // Phase 1.7 — UNION the two source feeds, newest first. Both queries
  // are tenant-scoped via RLS + status-filtered; merge happens client-
  // side. Phase 1 cardinality keeps this trivial; Era B may push merge
  // server-side if volume warrants.
  const editsRows = editsQuery.data?.rows ?? [];
  const resourceRows = resourcesQuery.data?.rows ?? [];
  const unionRows: UnionRow[] = useMemo(() => {
    const merged: UnionRow[] = [
      ...editsRows.map(
        (r): UnionRow => ({ kind: 'edit', row: r, created_at: r.created_at }),
      ),
      ...resourceRows.map(
        (r): UnionRow => ({ kind: 'resource', row: r, created_at: r.created_at }),
      ),
    ];
    merged.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return merged;
  }, [editsRows, resourceRows]);

  const isLoading = editsQuery.isPending || resourcesQuery.isPending;
  const isError = editsQuery.isError || resourcesQuery.isError;
  const isSuccess = editsQuery.isSuccess && resourcesQuery.isSuccess;
  const error = editsQuery.error ?? resourcesQuery.error;

  return (
    <div className="px-3 py-4 max-w-3xl mx-auto">
      <h2 className="font-display text-lg text-sage-800 mb-3">Review queue</h2>

      <div
        role="tablist"
        aria-label="Filter by status"
        className={`flex gap-1 mb-3 ${TAB_RAIL_CLASSES.tablist}`}
      >
        {VALID_STATUSES.map((status) => {
          const isActive = status === activeStatus;
          return (
            <button
              key={status}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => handleStatusChange(status)}
              className={
                `${TAB_RAIL_CLASSES.tab} ` +
                (isActive
                  ? `border-warm-400 ${TAB_RAIL_CLASSES.active}`
                  : TAB_RAIL_CLASSES.inactive)
              }
            >
              {STATUS_LABELS[status]}
            </button>
          );
        })}
      </div>

      <div className="text-xs text-ink-soft mb-3">
        {isSuccess && (
          <>
            {unionRows.length} {STATUS_LABELS[activeStatus].toLowerCase()} ·{' '}
            <span className="text-ink-soft">
              {editsRows.length} edit{editsRows.length === 1 ? '' : 's'},{' '}
              {resourceRows.length} new resource{resourceRows.length === 1 ? '' : 's'}
            </span>
          </>
        )}
      </div>

      <AsyncBoundary
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRetry={() => {
          void editsQuery.refetch();
          void resourcesQuery.refetch();
        }}
        isEmpty={isSuccess && unionRows.length === 0}
        emptyHeadline="No suggestions"
      >
      {isSuccess &&
        unionRows.map((row) =>
          row.kind === 'edit' ? (
            <SuggestionRow
              key={`edit-${row.row.id}`}
              suggestion={row.row}
              variant="queue"
              renderExpanded={() => (
                <SuggestionReviewPane
                  tenantSlug={tenantSlug}
                  suggestion={row.row}
                  invalidationKey={editsInvalidationKey}
                />
              )}
            />
          ) : (
            <ResourceProposalRow
              key={`resource-${row.row.id}`}
              proposal={row.row}
              renderExpanded={() => (
                <ResourceProposalReviewPane
                  tenantSlug={tenantSlug}
                  proposal={row.row}
                  invalidationKey={resourcesInvalidationKey}
                />
              )}
            />
          ),
        )}
      </AsyncBoundary>
    </div>
  );
}
